/**
 * Delhi NCR PIN validation for Fresh Flower Point delivery checker UI.
 * 110xxx — Delhi · 121xxx — Palwal & Faridabad corridor · 122xxx — Gurgaon · 201xxx — Noida
 */
const DELHI_NCR_PREFIXES = ['110', '121', '122', '201'];

/**
 * @param {string} pin
 * @returns {boolean}
 */
export function isDelhiNcrPin(pin) {
  const normalized = String(pin).replace(/\D/g, '');
  if (normalized.length !== 6) return false;
  const prefix = normalized.slice(0, 3);
  return DELHI_NCR_PREFIXES.includes(prefix);
}

export const PIN_STORAGE_KEY = 'ffp_validated_pin';

/**
 * @returns {string | null}
 */
export function getStoredPin() {
  try {
    return localStorage.getItem(PIN_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * @param {string} pin
 */
export function storeValidatedPin(pin) {
  const normalized = String(pin).replace(/\D/g, '');
  try {
    localStorage.setItem(PIN_STORAGE_KEY, normalized);
  } catch {
    /* ignore */
  }
  document.dispatchEvent(new CustomEvent('ffp:pin-updated', { detail: { pin: normalized } }));
  syncMobileBarState(normalized);
}

/**
 * Updates the sticky mobile bar immediately after validation. Runs here so the
 * UI still flips even if ffp-mobile-bar.js is slow to load or its listener
 * misses the event.
 * @param {string} pin
 */
function syncMobileBarState(pin) {
  const bar = document.getElementById('ffp-mobile-bar');
  if (!bar) return;

  const normalized = String(pin).replace(/\D/g, '');
  if (!isDelhiNcrPin(normalized)) return;

  bar.dataset.state = 'confirmed';
  bar.removeAttribute('data-hidden');

  const chip = bar.querySelector('[data-ffp-pin-chip]');
  if (chip) chip.textContent = `You can order · Next-day to ${normalized}`;

  const height = `${bar.offsetHeight}px`;
  document.body.style.setProperty('--ffp-mobile-bar-height', height);
}

class FfpDeliveryChecker extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-ffp-pin-input]');
    this.submit = this.querySelector('[data-ffp-pin-submit]');
    this.message = this.querySelector('[data-ffp-pin-message]');

    this.submit?.addEventListener('click', () => this.validate());
    this.input?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.validate();
      }
    });
    this.input?.addEventListener('input', () => {
      delete this.dataset.confirmed;
      if (this.message && !this.closest('.ffp-delivery-order-card')) {
        this.message.hidden = true;
        this.message.dataset.state = '';
      }

      const normalized = (this.input?.value ?? '').replace(/\D/g, '');
      if (normalized.length === 6) {
        this.validate();
      }
    });

    this.onPinUpdated = (event) => {
      const pin = event.detail?.pin ?? '';
      if (isDelhiNcrPin(pin)) this.showConfirmedState(pin);
    };
    document.addEventListener('ffp:pin-updated', this.onPinUpdated);

    const stored = getStoredPin();
    if (stored && isDelhiNcrPin(stored)) {
      this.showConfirmedState(stored);
      syncMobileBarState(stored);
    }
  }

  disconnectedCallback() {
    if (this.onPinUpdated) document.removeEventListener('ffp:pin-updated', this.onPinUpdated);
  }

  /**
   * Renders the success state without persisting, so a restore on page load and
   * a fresh validation produce identical UI.
   * @param {string} pin
   */
  showConfirmedState(pin) {
    const normalized = String(pin).replace(/\D/g, '');
    this.dataset.confirmed = 'true';
    if (this.input) this.input.value = normalized;

    if (!this.message) return;
    const areaHint = normalized.slice(0, 3) === '121' ? ' including Palwal' : '';
    this.message.hidden = false;
    this.message.dataset.state = 'success';
    this.message.textContent = `You can order now · Delivery to ${normalized}${areaHint} · Order at least 1 day before delivery`;
  }

  validate() {
    const normalized = (this.input?.value ?? '').replace(/\D/g, '');

    if (isDelhiNcrPin(normalized)) {
      this.showConfirmedState(normalized);
      storeValidatedPin(normalized);
      this.input?.blur();
      document.dispatchEvent(
        new CustomEvent('ffp:pin-validation', { detail: { pin: normalized, valid: true } })
      );
      return;
    }

    document.dispatchEvent(
      new CustomEvent('ffp:pin-validation', { detail: { pin: normalized, valid: false } })
    );

    if (!this.message) return;

    this.message.hidden = false;
    this.message.dataset.state = 'warning';
    this.message.textContent =
      normalized.length === 6
        ? 'We deliver in Delhi NCR only (110/121/122/201 PIN codes)'
        : 'Enter a valid 6-digit PIN code';
  }
}

if (!customElements.get('ffp-delivery-checker')) {
  customElements.define('ffp-delivery-checker', FfpDeliveryChecker);
}
